import { Badge, Button, Checkbox, Group, Modal, Paper, ScrollArea, Stack, Table, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDeviceFloppy, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { api, unwrap } from "../../api/client";
import { useT } from "../../i18n/script";
import AdminLayout from "./AdminLayout";

/**
 * Matrica: poslovne role (kolone) x permisije/funkcije (redovi).
 * Svaka promjena se upisuje u Keycloak kao composite rola -> vazi odmah za nove tokene.
 */
export default function RolesPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const roles = useQuery({ queryKey: ["admin-roles"], queryFn: async () => unwrap(await api.GET("/api/v1/admin/roles")) });
  const perms = useQuery({ queryKey: ["admin-perms"], queryFn: async () => unwrap(await api.GET("/api/v1/admin/permissions")) });
  const [matrix, setMatrix] = useState<Record<string, Set<string>>>({});
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [newRole, setNewRole] = useState<{ name: string; description: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (roles.data) {
      setMatrix(Object.fromEntries(roles.data.map((r) => [r.name, new Set(r.permissions)])));
      setDirty(new Set());
    }
  }, [roles.data]);

  const byModule = useMemo(() => {
    const m: Record<string, typeof perms.data> = {};
    const f = filter.trim().toLowerCase();
    (perms.data ?? []).filter((p) => !f || `${p.name} ${p.description ?? ""} ${p.module}`.toLowerCase().includes(f))
      .forEach((p) => (m[p.module] ??= []).push(p));
    return m;
  }, [perms.data, filter]);

  const toggle = (role: string, perm: string) => {
    setMatrix((m) => {
      const s = new Set(m[role]);
      s.has(perm) ? s.delete(perm) : s.add(perm);
      return { ...m, [role]: s };
    });
    setDirty((d) => new Set(d).add(role));
  };

  async function save() {
    setBusy(true);
    try {
      for (const role of dirty) {
        unwrap(await api.PATCH("/api/v1/admin/roles/{name}", { params: { path: { name: role } },
          body: { permissions: [...matrix[role]] } }));
      }
      notifications.show({ color: "green", message: t("Ovlašćenja sačuvana u Keycloak-u") });
      qc.invalidateQueries({ queryKey: ["admin-roles"] });
    } catch (e) { notifications.show({ color: "red", message: String(e) }); } finally { setBusy(false); }
  }

  async function create() {
    if (!newRole) return;
    try {
      unwrap(await api.POST("/api/v1/admin/roles", { body: { ...newRole, permissions: ["map:view"] } }));
      setNewRole(null);
      qc.invalidateQueries({ queryKey: ["admin-roles"] });
    } catch (e) { notifications.show({ color: "red", message: String(e) }); }
  }

  async function remove(name: string) {
    if (!window.confirm(t("Obrisati rolu") + ` ${name}?`)) return;
    try {
      unwrap(await api.DELETE("/api/v1/admin/roles/{name}", { params: { path: { name } } }));
      qc.invalidateQueries({ queryKey: ["admin-roles"] });
    } catch (e) { notifications.show({ color: "red", message: String(e) }); }
  }

  const list = roles.data ?? [];
  return (
    <AdminLayout>
      <Stack>
        <Group justify="space-between">
          <div>
            <Title order={3}>{t("Role i ovlašćenja")}</Title>
            <Text size="sm" c="dimmed">{t("Svaka permisija je jedna funkcija API-ja. Pristup slojevima podataka se dodatno ograničava pravilima u modulu Pristup podacima (GeoFence).")}</Text>
          </div>
          <Group>
            <TextInput placeholder={t("Pretraga funkcija")} leftSection={<IconSearch size={15} />} value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)} w={220} />
            <Button variant="default" leftSection={<IconPlus size={16} />} onClick={() => setNewRole({ name: "", description: "" })}>{t("Nova rola")}</Button>
            <Button leftSection={<IconDeviceFloppy size={16} />} disabled={!dirty.size} loading={busy} onClick={save}>
              {t("Sačuvaj")} {dirty.size ? `(${dirty.size})` : ""}
            </Button>
          </Group>
        </Group>
        <Paper withBorder>
          <ScrollArea>
            <Table stickyHeader verticalSpacing={6} withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th miw={260}>{t("Funkcija / permisija")}</Table.Th>
                  {list.map((r) => (
                    <Table.Th key={r.name} ta="center" miw={120} bg={dirty.has(r.name) ? "var(--mantine-color-orange-0)" : undefined}>
                      <Tooltip label={r.description}><Text size="xs" fw={700} translate="no">{r.name}</Text></Tooltip>
                      {r.description && <Text size="10px" c="dimmed" lineClamp={2} maw={140} mx="auto">{r.description}</Text>}
                      <Group gap={4} justify="center">
                        <Badge size="xs" variant="light">{r.user_count ?? 0} {t("kor.")}</Badge>
                        {!["administrator", "javni_korisnik", "registrovani_korisnik"].includes(r.name) && (
                          <IconTrash size={12} style={{ cursor: "pointer" }} color="#c0392b" onClick={() => remove(r.name)} />
                        )}
                      </Group>
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {Object.entries(byModule).map(([mod, ps]) => [
                  <Table.Tr key={mod} bg="var(--mantine-color-gray-1)">
                    <Table.Td colSpan={list.length + 1}><Text size="xs" fw={700} tt="uppercase" c="dimmed" translate="no">{mod}</Text></Table.Td>
                  </Table.Tr>,
                  ...(ps ?? []).map((p) => (
                    <Table.Tr key={p.name}>
                      <Table.Td><Text size="sm">{t(p.description ?? p.name)}</Text><Text size="xs" c="dimmed" ff="monospace" translate="no">{p.name}</Text></Table.Td>
                      {list.map((r) => (
                        <Table.Td key={r.name} ta="center">
                          <Checkbox mx="auto" w="fit-content" checked={matrix[r.name]?.has(p.name) ?? false}
                            onChange={() => toggle(r.name, p.name)} disabled={r.name === "administrator"} />
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  )),
                ])}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
        <Modal opened={!!newRole} onClose={() => setNewRole(null)} title={t("Nova poslovna rola")}>
          <Stack>
            <TextInput label={t("Naziv (mala slova, bez razmaka)")} placeholder="kontrolor_podsticaja" value={newRole?.name ?? ""}
              onChange={(e) => setNewRole((r) => ({ ...r!, name: e.currentTarget.value }))} />
            <TextInput label={t("Opis")} value={newRole?.description ?? ""}
              onChange={(e) => setNewRole((r) => ({ ...r!, description: e.currentTarget.value }))} />
            <Button onClick={create}>{t("Kreiraj")}</Button>
          </Stack>
        </Modal>
      </Stack>
    </AdminLayout>
  );
}
